import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { pb } from '../lib/pb';
import { errorMessage } from '../lib/errors';
import { toast } from '../store/store';
import { getData, loadGameData } from '../data/dataStore';
import type { DataImportPlan, UnityDataExport } from '../data/types';

const ICON_UPLOADS_IN_PARALLEL = 4;
// The server's JSON key order isn't stable, so counts are listed in a fixed order.
const COUNT_ORDER = [
  'assets',
  'adopted',
  'inSync',
  'fastForwarded',
  'designAhead',
  'conflicts',
  'missing',
  'skipped',
];

/** Admin → Game data: upload the Unity "Export game data" folder (unity-data.json + icons/*.png). */
export function DataImportAdmin() {
  const { t } = useTranslation();
  const [payload, setPayload] = useState<UnityDataExport | null>(null);
  const [iconFiles, setIconFiles] = useState<File[]>([]);
  const [plan, setPlan] = useState<DataImportPlan | null>(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<string | null>(null);

  const readJson = async (file: File | undefined) => {
    setPlan(null);
    if (!file) return setPayload(null);
    try {
      const json = JSON.parse(await file.text()) as UnityDataExport;
      if (json.schema !== 'eddan.unity.data') throw new Error('schema');
      setPayload(json);
    } catch {
      toast(t('admin.gamedata.badFile'), 'error');
      setPayload(null);
    }
  };

  const run = async (dryRun: boolean) => {
    if (!payload) return;
    setBusy(true);
    try {
      const res = await pb.send<DataImportPlan>(`/api/gamedata/import?dryRun=${dryRun ? 1 : 0}`, {
        method: 'POST',
        body: payload,
      });
      setPlan(res);
      if (!dryRun) {
        await uploadIcons();
        await loadGameData(true);
        toast(t('admin.gamedata.done'));
      }
    } catch (err) {
      toast(errorMessage(err), 'error');
    } finally {
      setBusy(false);
      setProgress(null);
    }
  };

  /** Creates or replaces one data_icons record per exported sprite that was selected. */
  const uploadIcons = async () => {
    const byName = new Map(iconFiles.map((f) => [f.name, f]));
    const todo = (payload?.icons ?? []).filter((i) => byName.has(i.file.split('/').pop() ?? ''));
    const existing = getData().icons;
    let done = 0;
    const queue = [...todo];
    const worker = async () => {
      for (let icon = queue.shift(); icon; icon = queue.shift()) {
        const fd = new FormData();
        fd.append('key', icon.key);
        fd.append('name', icon.name ?? '');
        fd.append('file', byName.get(icon.file.split('/').pop() ?? '')!);
        const prev = existing[icon.key];
        if (prev) await pb.collection('data_icons').update(prev.id, fd);
        else await pb.collection('data_icons').create(fd);
        setProgress(t('admin.gamedata.iconProgress', { done: ++done, total: todo.length }));
      }
    };
    await Promise.all(Array.from({ length: ICON_UPLOADS_IN_PARALLEL }, worker));
  };

  const matchedIcons = (payload?.icons ?? []).filter((i) =>
    iconFiles.some((f) => f.name === i.file.split('/').pop()),
  ).length;

  return (
    <div className="admin-import">
      <p className="muted small">{t('admin.gamedata.help')}</p>
      <label>
        unity-data.json
        <input
          type="file"
          accept="application/json,.json"
          onChange={(e) => void readJson(e.target.files?.[0])}
        />
      </label>
      <label>
        {t('admin.gamedata.icons')}
        <input
          type="file"
          accept="image/png"
          multiple
          onChange={(e) => setIconFiles(Array.from(e.target.files ?? []))}
        />
      </label>
      {payload && (
        <p className="small">
          {t('admin.gamedata.summary', {
            assets: payload.assets?.length ?? 0,
            icons: payload.icons?.length ?? 0,
          })}{' '}
          · {payload.exportedAt} · <span className="mono">{payload.git?.commit?.slice(0, 8)}</span>
          {payload.git?.dirty ? ` (${t('admin.import.dirty')})` : ''}
          {iconFiles.length > 0 && (
            <>
              <br />
              {t('admin.gamedata.iconsMatched', { matched: matchedIcons, total: payload.icons?.length ?? 0 })}
            </>
          )}
        </p>
      )}
      <div className="row">
        <button disabled={!payload || busy} onClick={() => void run(true)}>
          {t('admin.import.dryRun')}
        </button>
        <button
          className="primary"
          disabled={!payload || busy || !plan?.dryRun}
          onClick={() => void run(false)}
        >
          {t('admin.import.apply')}
        </button>
      </div>
      {progress && <p className="small muted">{progress}</p>}
      {plan && <DataPlanView plan={plan} />}
    </div>
  );
}

function DataPlanView({ plan }: { plan: DataImportPlan }) {
  const { t } = useTranslation();
  const s = plan.schema;
  const schemaChanges =
    s.classesAdded.length +
    s.classesRemoved.length +
    s.added.length +
    s.removed.length +
    s.renamed.length +
    s.changed.length +
    s.enums.length;
  const short = (c: string) => c.split('.').pop();
  return (
    <div className="plan">
      <h4>{plan.dryRun ? t('admin.import.planTitle') : t('admin.import.resultTitle')}</h4>
      <dl className="stats">
        {COUNT_ORDER.filter((k) => k in plan.counts).map((k) => (
          <div key={k} className="stat-pair">
            <dt>{t(`admin.gamedata.counts.${k}`)}</dt>
            <dd>{plan.counts[k]}</dd>
          </div>
        ))}
      </dl>
      {plan.initial ? (
        <p className="small muted">{t('admin.gamedata.initial')}</p>
      ) : (
        schemaChanges > 0 && (
          <div className="card">
            <h4>{t('admin.gamedata.schemaTitle')}</h4>
            <ul className="small">
              {s.classesAdded.map((c) => (
                <li key={'ca' + c}>{t('admin.gamedata.classAdded', { name: short(c) })}</li>
              ))}
              {s.classesRemoved.map((c) => (
                <li key={'cr' + c}>{t('admin.gamedata.classRemoved', { name: short(c) })}</li>
              ))}
              {s.added.map((f) => (
                <li key={'a' + f.owner + f.name}>
                  {t('admin.gamedata.fieldAdded', { owner: short(f.owner), name: f.name, kind: f.kind })}
                </li>
              ))}
              {s.renamed.map((f) => (
                <li key={'rn' + f.owner + f.from}>
                  {t('admin.gamedata.fieldRenamed', { owner: short(f.owner), from: f.from, to: f.to })}
                </li>
              ))}
              {s.removed.map((f) => (
                <li key={'rm' + f.owner + f.name}>
                  {t('admin.gamedata.fieldRemoved', { owner: short(f.owner), name: f.name })}
                </li>
              ))}
              {s.changed.map((f) => (
                <li key={'ch' + f.owner + f.name}>
                  {t('admin.gamedata.fieldChanged', {
                    owner: short(f.owner),
                    name: f.name,
                    from: f.from,
                    to: f.to,
                  })}
                </li>
              ))}
              {s.enums.map((e) => (
                <li key={'e' + e.enum}>
                  {t('admin.gamedata.enumChanged', {
                    name: short(e.enum),
                    added: e.added.join(', ') || '—',
                    removed: e.removed.join(', ') || '—',
                  })}
                </li>
              ))}
            </ul>
            {s.added.length > 0 && <p className="small muted">{t('admin.gamedata.reviewNew')}</p>}
          </div>
        )
      )}
      {plan.items.length > 0 && (
        <details open={!plan.initial}>
          <summary>
            {t('admin.gamedata.changes')} ({plan.items.length})
          </summary>
          <table className="vertices small">
            <tbody>
              {plan.items.slice(0, 300).map((it, i) => (
                <tr key={i}>
                  <td>
                    <span className={`sync-pill ${it.action}`}>
                      {t(`admin.gamedata.actions.${it.action}`, { defaultValue: it.action })}
                    </span>
                  </td>
                  <td>{t(`data.categories.${it.category}`, { defaultValue: it.category })}</td>
                  <td className="ellipsis" title={it.fields?.join(', ')}>
                    {it.name}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}
      {plan.warnings.length > 0 && (
        <details>
          <summary>
            {t('admin.import.warnings')} ({plan.warnings.length})
          </summary>
          <ul className="small">
            {plan.warnings.map((w, i) => (
              <li key={i}>{w}</li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}
